// Pure helpers for the field engineer task tracker (KOP-40 spec §5.1). No Vue, no axios; `today` is injected.
import { isoToRu, unixToRu, isOverdue, toNumber } from '@/components/management/dispatcher/format';
import { shiftIso } from '@/components/management/dispatcher/period';
import { toLatLng } from '@/components/management/dispatcher/objectState';

export const TABS = Object.freeze({ ACTIVE: 'active', DONE: 'done' });
export const DEFAULT_DAYS = 7;
export const TAB_LABELS = Object.freeze({
  active: Object.freeze({ long: 'Активные задачи', short: 'Активные' }),
  done: Object.freeze({ long: 'Завершенные задачи', short: 'Завершенные' }),
});
export const PARTIAL_BADGE = 'Не выполнена частично';
export const OVERDUE_BADGE = 'Просрочена';
export const ACTIVE_EMPTY = 'Активных задач нет';
export const DONE_EMPTY = 'За выбранный период задач нет';
export const CONSUMABLES_EMPTY = 'Расходники не требуются';

const NB_HYPHEN = '‑';

export function readTab(query) {
  return query && query.tab === TABS.DONE ? TABS.DONE : TABS.ACTIVE;
}

export function defaultPeriod(today) {
  return { from: shiftIso(today, -DEFAULT_DAYS), to: today };
}

export function taskLabel(id) {
  return `T${NB_HYPHEN}${id}`;
}

/** Works of a task as `{ id, title, done }`, ordered by id; `done` only for a strict `true`. */
export function workRows(fieldWorks) {
  return (Array.isArray(fieldWorks) ? fieldWorks : [])
    .map((w) => ({ id: w.id, title: (w.type && w.type.title) || '—', done: w.is_done === true }))
    .sort((a, b) => a.id - b.id);
}

export function hasUndone(works) {
  return (Array.isArray(works) ? works : []).some((w) => !w.done);
}

/** «Не выполнена частично»: a done task with at least one unfinished work (spec §2 #10). */
export function isPartial(task) {
  const code = task && task.status && task.status.code;
  return code === 'done' && hasUndone(workRows(task && task.field_works));
}

export function yandexRouteUrl(latLng) {
  if (!latLng) return null;
  return `https://yandex.ru/maps/?rtext=~${latLng[0]},${latLng[1]}&rtt=auto`;
}

function trimmed(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function baseModel(task) {
  const loc = task.geo_location || null;
  return {
    id: task.id,
    label: taskLabel(task.id),
    objectName: (loc && loc.name) || '—',
    address: trimmed(loc && loc.address),
    works: workRows(task.field_works),
    dispatcherComment: trimmed(task.description),
  };
}

export function activeCardModel(task, today) {
  const loc = task.geo_location || null;
  const deadlineIso = task.deadline_date || null;
  return {
    ...baseModel(task),
    routeUrl: yandexRouteUrl(toLatLng(loc)),
    deadlineIso,
    deadline: deadlineIso ? isoToRu(deadlineIso) : null,
    overdue: deadlineIso !== null && isOverdue(deadlineIso, today),
    paper: toNumber(loc && loc.paper_balance),
    geoLocationId: task.geo_location_id == null ? null : task.geo_location_id,
  };
}

export function completedCardModel(task) {
  const ts = Number(task.completed_at);
  const closedTs = Number.isFinite(ts) && ts > 0 ? ts : null;
  return {
    ...baseModel(task),
    closedTs,
    closedAt: closedTs ? unixToRu(closedTs) : '—',
    partial: isPartial(task),
    engineerComment: trimmed(task.completion_comment),
  };
}

/** Deadline ascending, tasks without a deadline last, then id ascending. Returns a new array. */
export function sortActive(cards) {
  return [...cards].sort((a, b) => {
    if (a.deadlineIso !== b.deadlineIso) {
      if (a.deadlineIso === null) return 1;
      if (b.deadlineIso === null) return -1;
      return a.deadlineIso < b.deadlineIso ? -1 : 1;
    }
    return a.id - b.id;
  });
}

/** Newest completion first, unknown time last, then id descending. Returns a new array. */
export function sortCompleted(cards) {
  return [...cards].sort((a, b) => {
    const ta = a.closedTs || 0;
    const tb = b.closedTs || 0;
    if (ta !== tb) return tb - ta;
    return b.id - a.id;
  });
}

/** Pick-list rows from `get-assigned-consumables`; zero or non-numeric quantities are dropped. */
export function consumableRows(list) {
  return (Array.isArray(list) ? list : [])
    .map((c) => ({ id: c.id, title: c.title || '—', quantity: toNumber(c.quantity) }))
    .filter((r) => r.quantity !== null && r.quantity > 0);
}
